import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { IconRail } from '@/features/reader/IconRail';
import { comingFriday, formatFriday, shiftWeeks } from '@/features/admin/fridays';
import { useUrlParam } from '@/features/reader/useUrlState';
import { fetchCalendar, fetchKhutbahs, type KhutbahCard } from './homeApi';

/**
 * Home: what is this Friday's khutbah, and can I read it in my language yet?
 *
 * The reader arrives on a phone, often on Friday itself, often before the
 * khutbah has been published or translated. **The "not yet" states are not
 * edge cases** — for part of every week they are the only state — so they are
 * written as ordinary information rather than as errors.
 */
export function HomeScreen() {
  const [friday, setFriday] = useState(() => comingFriday());
  const [query, setQuery] = useState('');
  // In the URL, so the rail's Browse entry lands on browse and a reader can
  // share "the search I was looking at" — the standards' rule about
  // shareable navigation state.
  const [browseParam, setBrowseParam] = useUrlParam('browse', '');
  const browsing = browseParam === '1';
  const setBrowsing = (on: boolean) => setBrowseParam(on ? '1' : '');

  const thisFriday = useQuery({
    queryKey: ['khutbahs', 'friday', friday],
    queryFn: ({ signal }) => fetchKhutbahs({ from: friday, to: friday }, signal),
  });

  // A window around the chosen Friday, for the strip of upcoming weeks.
  const window = useQuery({
    queryKey: ['calendar', friday],
    queryFn: ({ signal }) =>
      fetchCalendar(shiftWeeks(friday, -6), shiftWeeks(friday, 6), signal),
  });

  // What exists at all, so an empty Friday can point somewhere instead of
  // being a dead end. For most of the week "nothing this Friday" is the
  // truthful answer, and it should still leave the reader somewhere to go.
  const latest = useQuery({
    queryKey: ['khutbahs', 'latest'],
    queryFn: ({ signal }) => fetchKhutbahs({}, signal),
    staleTime: 5 * 60 * 1000,
  });

  const results = useQuery({
    queryKey: ['khutbahs', 'search', query],
    queryFn: ({ signal }) => fetchKhutbahs({ q: query }, signal),
    enabled: browsing && query.trim().length > 1,
  });

  return (
    // The same shell shape as the reader: viewport height, chrome fixed, one
    // scrolling region. The two screens used different constraints -- the
    // reader filled a 1600px viewport with a three-column shell while Home
    // centred a 900px column and left 644px empty -- so the application
    // appeared to change width as you moved between them.
    <div className="flex h-[calc(100dvh-var(--app-bar-height))] overflow-hidden bg-background text-foreground">
      <IconRail current="read" />

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/*
          Full width, because this screen is cards and lists rather than
          paragraphs: a card grid uses a wide display well, where a column of
          prose would not. `max-w-7xl` stops it sprawling without pretending
          the display is 900px wide.
        */}
        <main className="flex min-h-0 flex-col w-full flex-1 overflow-y-auto px-4 py-8 sm:px-6">
          <div className="mx-auto w-full max-w-7xl">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-2xl font-semibold">This Friday</h1>
            <Button
              type="button"
              variant={browsing ? 'secondary' : 'outline'}
              size="lg"
              onClick={() => setBrowsing(!browsing)}
            >
              {browsing ? 'Back to this Friday' : 'Browse all'}
            </Button>
          </div>

          {browsing ? (
            <Browse
              query={query}
              onQuery={setQuery}
              results={results.data ?? []}
              searching={results.isFetching}
              ready={query.trim().length > 1}
            />
          ) : (
            <>
              <FridayNav friday={friday} onFriday={setFriday} />

              {thisFriday.isPending ? (
                <div className="mt-4 flex flex-col gap-3" aria-busy>
                  <Skeleton className="h-28" />
                  <Skeleton className="h-28" />
                </div>
              ) : thisFriday.error ? (
                <p role="alert" className="mt-4 rounded-lg border border-destructive p-4 text-sm">
                  Could not reach the server.
                </p>
              ) : thisFriday.data.length === 0 ? (
                <NotYet
                  friday={friday}
                  latest={latest.data?.[0] ?? null}
                  onGoToLatest={(to) => setFriday(to)}
                />
              ) : (
                <ul className="mt-4 flex flex-col gap-3">
                  {thisFriday.data.map((card) => (
                    <li key={card.khutbahId}>
                      <Card card={card} />
                    </li>
                  ))}
                </ul>
              )}

              <UpcomingStrip
                friday={friday}
                onFriday={setFriday}
                entries={window.data ?? []}
              />
            </>
          )}

          <p className="mt-8 text-xs text-muted-foreground">
            Unofficial reading aid · not endorsed by the publisher.
          </p>
          </div>
        </main>
      </div>
    </div>
  );
}

function FridayNav({
  friday,
  onFriday,
}: {
  friday: string;
  onFriday: (next: string) => void;
}) {
  return (
    <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-border p-2">
      <Button
        type="button"
        variant="ghost"
        size="lg"
        onClick={() => onFriday(shiftWeeks(friday, -1))}
      >
        ‹ Previous
      </Button>
      <span className="text-center text-sm font-semibold" aria-live="polite">
        {formatFriday(friday)}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="lg"
        onClick={() => onFriday(shiftWeeks(friday, 1))}
      >
        Next ›
      </Button>
    </div>
  );
}

/**
 * The khutbah is not out yet — which for part of every week is simply the
 * truth, and must not be styled as a failure.
 */
function NotYet({
  friday,
  latest,
  onGoToLatest,
}: {
  friday: string;
  latest: KhutbahCard | null;
  onGoToLatest: (friday: string) => void;
}) {
  return (
    <div className="mt-4 rounded-lg border border-dashed border-border p-8 text-center">
      <p className="font-semibold">Nothing published for {formatFriday(friday)} yet</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-pretty text-muted-foreground">
        The ministries usually publish during the week before.
      </p>

      {/* Not a dead end. For most of the week this is the only state, and
          leaving a reader with nowhere to go is how a working product looks
          broken. */}
      {latest ? (
        <div className="mt-4 flex flex-col items-center gap-2">
          <p className="text-sm text-muted-foreground">
            The most recent one is {formatFriday(latest.friday)}:
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button type="button" size="lg" asChild>
              <a href={`?khutbah=${encodeURIComponent(latest.khutbahId)}`}>
                Read it
              </a>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => onGoToLatest(latest.friday)}
            >
              Go to that Friday
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Card({ card }: { card: KhutbahCard }) {
  return (
    <article className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span
          aria-hidden
          className={cn(
            'inline-block size-2 rounded-full',
            card.institutionKey === 'uae-awqaf'
              ? 'bg-institution-uae'
              : 'bg-institution-oman',
          )}
        />
        {card.institutionNameAr ? (
          <span lang="ar" dir="rtl" className="script-ar">
            {card.institutionNameAr}
          </span>
        ) : (
          card.institutionName
        )}
      </div>

      {/*
        The subject, in a language the reader has and in the one it was
        preached in. A list of Arabic titles tells someone who does not read
        Arabic nothing about which khutbah is which — which is the whole
        problem this product exists for.

        The translated line leads and the Arabic sits under it, smaller: this
        is a list someone scans to choose, and they scan it in their own
        language. In the reader the order reverses, because there the Arabic
        is the text and the translation is the aid.

        No fallback. A khutbah whose title nobody has translated shows the
        Arabic alone rather than a rendering we invented or a repeat of the
        same string pretending to be a translation.
      */}
      {card.titleTranslated ? (
        <h2 className="mt-2 text-xl font-medium text-pretty">{card.titleTranslated}</h2>
      ) : null}

      {card.titleOriginal ? (
        <p
          lang="ar"
          dir="rtl"
          className={cn(
            'script-ar font-normal',
            card.titleTranslated
              ? 'mt-1 text-base text-muted-foreground'
              : 'mt-2 text-xl',
          )}
        >
          {card.titleOriginal}
        </p>
      ) : card.titleTranslated ? null : (
        <h2 className="mt-2 text-lg italic text-muted-foreground">Untitled</h2>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {card.languages.length === 0 ? (
          // The common state early in the week: the Arabic is out and
          // nobody has translated it. Said, not left blank.
          <span className="text-xs text-muted-foreground">
            Not translated yet — the Arabic is readable now.
          </span>
        ) : (
          card.languages.map((language) => (
            <span
              key={language.code}
              className={cn(
                'inline-flex items-center gap-1 rounded-sm px-2 py-1 text-[12px] font-semibold',
                language.state === 'reviewed'
                  ? 'bg-status-reviewed-bg text-status-reviewed'
                  : 'bg-status-ai-bg text-status-ai',
              )}
            >
              {language.name}
              {/* Never shortened to a tick: "unreviewed" is the point. */}
              <span className="font-normal">
                {language.state === 'reviewed' ? '· reviewed' : '· unreviewed'}
              </span>
            </span>
          ))
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="button" size="lg" asChild>
          <a href={`?khutbah=${encodeURIComponent(card.khutbahId)}`}>Read</a>
        </Button>
        <Button type="button" variant="outline" size="lg" asChild>
          <a href={`?screen=learn&khutbah=${encodeURIComponent(card.khutbahId)}`}>
            Learn its Arabic
          </a>
        </Button>
      </div>
    </article>
  );
}

/** Which nearby Fridays have anything, so a reader can see the shape of it. */
function UpcomingStrip({
  friday,
  onFriday,
  entries,
}: {
  friday: string;
  onFriday: (next: string) => void;
  entries: readonly { friday: string; institutions: readonly string[] }[];
}) {
  const weeks = Array.from({ length: 7 }, (_, i) => shiftWeeks(friday, i - 3));
  const published = new Map(entries.map((entry) => [entry.friday, entry.institutions]));

  return (
    <section className="mt-6">
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Nearby Fridays
      </h2>
      <ul className="mt-2 flex flex-wrap gap-2">
        {weeks.map((week) => {
          const institutions = published.get(week) ?? [];
          return (
            <li key={week}>
              <button
                type="button"
                onClick={() => onFriday(week)}
                aria-current={week === friday ? 'true' : undefined}
                className={cn(
                  'flex min-h-11 flex-col items-center gap-1 rounded-md border px-3 py-1.5',
                  week === friday
                    ? 'border-primary bg-accent'
                    : 'border-border hover:bg-muted',
                )}
              >
                <span className="text-[13px] tabular-nums">{week.slice(5)}</span>
                <span className="flex gap-0.5" aria-hidden>
                  {institutions.length === 0 ? (
                    // Dashed and hollow: nothing published, which is not the
                    // same as nothing happening.
                    <span className="size-1.5 rounded-full border border-dashed border-muted-foreground" />
                  ) : (
                    institutions.map((key) => (
                      <span
                        key={key}
                        className={cn(
                          'size-1.5 rounded-full',
                          key === 'uae-awqaf'
                            ? 'bg-institution-uae'
                            : 'bg-institution-oman',
                        )}
                      />
                    ))
                  )}
                </span>
                <span className="sr-only">
                  {institutions.length === 0
                    ? 'nothing published'
                    : `${institutions.length} published`}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Browse({
  query,
  onQuery,
  results,
  searching,
  ready,
}: {
  query: string;
  onQuery: (next: string) => void;
  results: readonly KhutbahCard[];
  searching: boolean;
  ready: boolean;
}) {
  return (
    <div className="mt-4 flex flex-col gap-4">
      <label className="flex flex-col gap-1.5">
        <span className="text-[13px] font-medium">Search the Arabic titles</span>
        <input
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          placeholder="التقوى"
          className="h-11 w-full rounded-md border border-border bg-background px-3 outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
        />
        {/* Said plainly: Postgres has no stemming for any of this product's
            languages, so matching is approximate and promising otherwise
            would be a claim the search cannot keep. */}
        <span className="text-xs text-muted-foreground">
          Matching is approximate — there is no stemming for Arabic, so a
          different ending may not match.
        </span>
      </label>

      {!ready ? (
        <p className="text-sm text-muted-foreground">Type at least two characters.</p>
      ) : searching ? (
        <Skeleton className="h-28" />
      ) : results.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p className="font-semibold">Nothing matched “{query}”</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Try a shorter fragment, or a word without its prefix.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {results.map((card) => (
            <li key={card.khutbahId}>
              <Card card={card} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
